import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Input,
  Select,
  Stepper,
  Text,
} from 'barte-design-system';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface DrawerSolicitarMaquininhasProps {
  isOpen: boolean;
  onClose: () => void;
}

interface SellerAllocation {
  id: string;
  name: string;
  cnpj: string;
  qty: number;
}

// ─── Mock sellers ─────────────────────────────────────────────────────────────

const SELLERS_MOCK: Omit<SellerAllocation, 'qty'>[] = [
  { id: 'SEL-001', name: 'Loja Central LTDA',       cnpj: '12.345.678/0001-90' },
  { id: 'SEL-002', name: 'Mercado Bom Preço',        cnpj: '23.456.789/0001-01' },
  { id: 'SEL-003', name: 'Restaurante Sabor & Arte', cnpj: '34.567.890/0001-12' },
  { id: 'SEL-004', name: 'Posto Combustível Star',   cnpj: '45.678.901/0001-23' },
  { id: 'SEL-005', name: 'Farmácia Saúde & Vida',    cnpj: '56.789.012/0001-34' },
  { id: 'SEL-006', name: 'Padaria Pão Quente',       cnpj: '67.890.123/0001-45' },
  { id: 'SEL-007', name: 'Academia FitLife',          cnpj: '78.901.234/0001-56' },
  { id: 'SEL-008', name: 'Pet Shop Animal Love',     cnpj: '89.012.345/0001-67' },
];

// ─── Constants ────────────────────────────────────────────────────────────────

const TERMINAL_VERSION_OPTIONS = [
  { value: 'sunmi-d3-pro',      label: 'Sunmi D3 Pro' },
  { value: 'pax-a920',          label: 'PAX A920' },
  { value: 'ingenico-move5000', label: 'Ingenico Move 5000' },
  { value: 'gertec-gpos720',    label: 'Gertec GPOS 720' },
  { value: 'verifone-v240m',    label: 'Verifone V240m' },
];

const STEPS = [
  { label: 'Pedido' },
  { label: 'Alocação' },
];

// ─── Component ────────────────────────────────────────────────────────────────

export function DrawerSolicitarMaquininhas({
  isOpen,
  onClose,
}: DrawerSolicitarMaquininhasProps) {
  const [step, setStep] = useState(1);

  // Step 1 fields
  const [quantityRaw, setQuantityRaw] = useState('');
  const [version, setVersion]         = useState('');
  const [quantityError, setQuantityError] = useState('');
  const [versionError, setVersionError]   = useState('');

  // Step 2: sellers with individual qty allocation
  const [allocations, setAllocations] = useState<SellerAllocation[]>([]);

  // Reset when drawer opens
  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setQuantityRaw('');
      setVersion('');
      setQuantityError('');
      setVersionError('');
      setAllocations([]);
    }
  }, [isOpen]);

  // ── Derived values ──────────────────────────────────────────────────────────

  const quantity   = parseInt(quantityRaw, 10);
  const validQty   = !isNaN(quantity) && quantity >= 1;
  const totalAlloc = allocations.reduce((sum, s) => sum + s.qty, 0);
  const remaining  = validQty ? quantity - totalAlloc : 0;
  const allocOk    = validQty && totalAlloc === quantity;

  // ── Step navigation ─────────────────────────────────────────────────────────

  function handleStep1Next() {
    let hasError = false;

    if (!validQty) {
      setQuantityError('Informe uma quantidade válida (mínimo 1).');
      hasError = true;
    } else {
      setQuantityError('');
    }

    if (!version) {
      setVersionError('Selecione a versão do terminal.');
      hasError = true;
    } else {
      setVersionError('');
    }

    if (hasError) return;

    // Initialise allocations for step 2
    setAllocations(SELLERS_MOCK.map((s) => ({ ...s, qty: 0 })));
    setStep(2);
  }

  function handleStep2Back() {
    setStep(1);
  }

  function handleConfirm() {
    // In a real implementation, this would call an API
    onClose();
  }

  // ── Allocation controls ─────────────────────────────────────────────────────

  function increment(id: string) {
    if (remaining <= 0) return;
    setAllocations((prev) =>
      prev.map((s) => (s.id === id ? { ...s, qty: s.qty + 1 } : s)),
    );
  }

  function decrement(id: string) {
    setAllocations((prev) =>
      prev.map((s) =>
        s.id === id && s.qty > 0 ? { ...s, qty: s.qty - 1 } : s,
      ),
    );
  }

  // ── Drawer metadata ─────────────────────────────────────────────────────────

  const versionLabel =
    TERMINAL_VERSION_OPTIONS.find((o) => o.value === version)?.label ?? '';

  // ── Step 1 footer ────────────────────────────────────────────────────────────

  const step1Footer = {
    primaryButton: {
      children: 'Próximo',
      onClick: handleStep1Next,
    },
    secondaryButton: {
      children: 'Cancelar',
      onClick: onClose,
      variant: 'outline' as const,
    },
  };

  // ── Step 2 footer ────────────────────────────────────────────────────────────

  const step2Footer = {
    primaryButton: {
      children: 'Confirmar pedido',
      onClick: handleConfirm,
      disabled: !allocOk,
    },
    secondaryButton: {
      children: 'Voltar',
      onClick: handleStep2Back,
      variant: 'outline' as const,
    },
  };

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <Drawer
      isOpen={isOpen}
      title="Solicitar maquininhas"
      onClose={onClose}
      footer={step === 1 ? step1Footer : step2Footer}
    >
      {/* Stepper */}
      <Stepper
        steps={STEPS}
        currentStep={step}
        className="sm-drawer-stepper"
      />

      {/* ── STEP 1: Pedido ──────────────────────────────────────────────────── */}
      {step === 1 && (
        <div className="sm-drawer-form">
          <Input
            label="Quantidade de terminais"
            placeholder="Ex: 10"
            type="text"
            value={quantityRaw}
            onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
              // Allow only digits
              const val = e.target.value.replace(/\D/g, '');
              setQuantityRaw(val);
              if (quantityError) setQuantityError('');
            }}
            error={quantityError || undefined}
            helpText={!quantityError ? 'Mínimo 1 terminal por solicitação.' : undefined}
          />

          <Select
            label="Versão do terminal"
            placeholder="Selecione o modelo"
            options={TERMINAL_VERSION_OPTIONS}
            value={version}
            onChange={(val) => {
              setVersion(val);
              if (versionError) setVersionError('');
            }}
            helpText={
              !versionError
                ? 'O modelo determina o hardware enviado ao seller.'
                : undefined
            }
          />

          {/* Resumo quando ambos os campos estão preenchidos */}
          {validQty && version && (
            <div className="sm-summary-box">
              <div className="sm-summary-row">
                <Text variant="body-sm" style={{ color: 'var(--content-secondary)' }}>
                  Modelo selecionado
                </Text>
                <Text variant="label-sm">{versionLabel}</Text>
              </div>
              <div className="sm-summary-row">
                <Text variant="body-sm" style={{ color: 'var(--content-secondary)' }}>
                  Quantidade
                </Text>
                <Text variant="label-sm">{quantity} terminal{quantity !== 1 ? 'is' : ''}</Text>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── STEP 2: Alocação de sellers ─────────────────────────────────────── */}
      {step === 2 && (
        <div className="sm-drawer-form">
          {/* Cabeçalho com contador */}
          <div className="sm-alloc-header">
            <div>
              <Text variant="label-base">Alocar terminais aos sellers</Text>
              <Text variant="body-sm" style={{ color: 'var(--content-secondary)', marginTop: 2 }}>
                Distribua os {quantity} terminais ({versionLabel}) entre seus sellers.
              </Text>
            </div>
            <div className="sm-alloc-counter">
              <Text
                variant="label-sm"
                className={allocOk ? 'sm-alloc-counter-ok' : remaining < 0 ? 'sm-alloc-counter-error' : ''}
              >
                {totalAlloc}/{quantity}
              </Text>
              <Text variant="body-sm" style={{ color: 'var(--content-tertiary)' }}>
                alocados
              </Text>
            </div>
          </div>

          {/* Lista de sellers */}
          <div className="sm-alloc-list">
            {allocations.map((seller) => (
              <div key={seller.id} className="sm-alloc-row">
                {/* Seller info */}
                <div className="sm-alloc-seller">
                  <Text variant="label-sm" className="sm-alloc-seller-name">
                    {seller.name}
                  </Text>
                  <Text variant="body-xs" className="sm-alloc-seller-id">
                    {seller.id} · {seller.cnpj}
                  </Text>
                </div>

                {/* Controle +/- */}
                <div className="sm-qty-control">
                  <button
                    className="sm-qty-btn"
                    onClick={() => decrement(seller.id)}
                    disabled={seller.qty === 0}
                    aria-label={`Diminuir terminais de ${seller.name}`}
                  >
                    –
                  </button>
                  <Text variant="label-sm" className="sm-qty-value">
                    {seller.qty}
                  </Text>
                  <button
                    className="sm-qty-btn"
                    onClick={() => increment(seller.id)}
                    disabled={remaining <= 0}
                    aria-label={`Aumentar terminais de ${seller.name}`}
                  >
                    +
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Aviso se ainda houver terminais a alocar */}
          {!allocOk && totalAlloc < quantity && (
            <Text variant="body-sm" style={{ color: 'var(--content-secondary)' }}>
              Ainda faltam{' '}
              <strong>{remaining} terminal{remaining !== 1 ? 'is' : ''}</strong>{' '}
              para alocar. Você pode deixar sellers com 0 terminais.
            </Text>
          )}
        </div>
      )}
    </Drawer>
  );
}
